import { ImageOff, CheckCircle2 } from 'lucide-react';
import type { Exercise, PlanEntry } from '../../types/exercise';
import { LwsBadge } from './LwsBadge';
import { BandColorChip } from './BandColorChip';
import { ExerciseIllustration } from './ExerciseIllustration';
import { useExerciseProgress } from '../../lib/useSetProgress';

interface ExerciseCardProps {
  exercise: Exercise;
  entry: PlanEntry;
  dayKey: string;
  onOpen: (exerciseId: string) => void;
}

export function ExerciseCard({
  exercise,
  entry,
  dayKey,
  onOpen,
}: ExerciseCardProps): JSX.Element {
  const setsLabel = `${entry.sets} × ${entry.reps}`;
  const lwsVariant = exercise.lwsNote ? 'caution' : 'default';
  const { done, isComplete } = useExerciseProgress(dayKey, exercise.id, entry.sets);

  return (
    <button
      type="button"
      onClick={() => onOpen(exercise.id)}
      aria-label={`${exercise.name} oeffnen — ${setsLabel}${isComplete ? ', abgeschlossen' : ''}`}
      className={`flex w-full items-start gap-3 rounded-xl border bg-white p-4 text-left transition-transform duration-[120ms] active:scale-[0.97] active:bg-zinc-100 focus-visible:outline-2 focus-visible:outline-emerald-500 focus-visible:outline-offset-2 ${
        isComplete ? 'border-emerald-500' : 'border-zinc-200'
      }`}
    >
      <span
        aria-hidden="true"
        className="relative flex size-16 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-zinc-200 bg-zinc-100 text-zinc-400"
      >
        {exercise.imagePath ? (
          <img
            src={exercise.imagePath}
            alt=""
            loading="lazy"
            className="size-full object-cover"
          />
        ) : exercise.iconKey ? (
          <ExerciseIllustration iconKey={exercise.iconKey} size="thumb" />
        ) : (
          <ImageOff className="size-6" />
        )}
        {isComplete ? (
          <CheckCircle2
            aria-hidden="true"
            className="absolute -right-1 -top-1 size-5 rounded-full bg-white text-emerald-600"
          />
        ) : null}
      </span>

      <span className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="truncate text-base font-semibold leading-tight text-zinc-900">
          {exercise.name}
        </span>
        <span className="truncate text-sm text-zinc-600">{exercise.muscles}</span>
        <span className="flex items-center gap-2">
          <span className="text-sm font-semibold tabular-nums text-zinc-900">
            {done.length}/{entry.sets} · {entry.reps}
          </span>
          {exercise.bandColor?.long ? (
            <BandColorChip color={exercise.bandColor.long} bandKind="long" compact />
          ) : null}
          {exercise.bandColor?.short ? (
            <BandColorChip color={exercise.bandColor.short} bandKind="short" compact />
          ) : null}
        </span>
      </span>

      <span className="shrink-0">
        <LwsBadge variant={lwsVariant} note={exercise.lwsNote} />
      </span>
    </button>
  );
}
